import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Lock } from "lucide-react";
import { Countdown, PageHero, Reveal, SectionHeading } from "@/components/motion";
import { API_URL } from "@/components/forms";
import { NotifyForm } from "@/components/forms";

const SIGNING_DAY = "2027-02-03T09:00:00-06:00"; // National Signing Day, Feb 3 2027

const FOOTBALL_SLOTS = ["QB", "RB", "WR", "TE", "OL", "DL", "LB", "DB", "SPEC"];
const HOOPS_SLOTS = ["GUARD", "WING", "POST"];

function initialsOf(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function SlotBoard({ title, slots, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-navy-800 p-6" data-testid={testid}>
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ice">{title}</p>
      <div className="mt-5 flex flex-wrap gap-2.5">
        {slots.map((s) => (
          <span
            key={s}
            className="flex items-center gap-2 rounded-lg border border-white/10 bg-navy-700 px-3.5 py-2 font-mono text-xs font-semibold tracking-wider text-white transition-colors hover:border-ice/50"
          >
            {s}
            <span className="flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] text-ice">
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-ice" /> Open
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RosterPage() {
  const [committed, setCommitted] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_URL}/roster/committed`)
      .then((r) => setCommitted(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <PageHero
        testid="roster-hero"
        eyebrow="Class of 2027"
        lines={[<>The roster</>, <span className="text-ice">is sealed.</span>]}
        sub="The Alabama Bulldogs roster will not be released until the conclusion of signing day specified for each sport — Football and Basketball. When the ink dries on the last letter of intent, the names go public right here."
      />

      {/* THE COMMITTED — public profiles */}
      {committed.length > 0 && (
        <section data-testid="committed-section" className="border-t border-white/5 py-24 sm:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <SectionHeading
              eyebrow="Early announcements"
              title={
                <>
                  The <span className="text-emerald-400">committed.</span>
                </>
              }
              description="The first signatures are in. Meet the Bulldogs who have committed to the 2027 classes — tap a card for film and full measurable stats."
            />
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {committed.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.08} className="h-full">
                  <Link
                    to={`/roster/${c.id}`}
                    data-testid={`committed-card-${c.id}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-navy-800 p-6 transition-colors hover:border-emerald-400/50"
                  >
                    <div className="flex items-start justify-between">
                      {c.photo_url ? (
                        <img
                          data-testid={`committed-photo-${c.id}`}
                          src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`}
                          alt={c.full_name}
                          className="h-14 w-14 rounded-2xl border border-ice/30 object-cover"
                        />
                      ) : (
                        <div className="grid h-14 w-14 place-items-center rounded-2xl border border-ice/30 bg-navy-600 font-display text-xl font-black text-silver">
                          {initialsOf(c.full_name)}
                        </div>
                      )}
                      {c.jersey && (
                        <span className="rounded-lg border border-crimson/40 bg-crimson/10 px-2.5 py-1 font-display text-lg font-black text-crimson-bright">
                          {c.jersey}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                      {c.full_name}
                    </h3>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-ice">
                      {c.position} · Class of {c.graduation_year}
                    </p>
                    {c.bio && <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">{c.bio}</p>}
                    <span className="mt-5 inline-flex items-center gap-2 border-t border-white/5 pt-4 font-display text-sm font-bold uppercase tracking-wider text-slate-400 transition-colors group-hover:text-emerald-300">
                      View profile <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LOCKED NOTICE + COUNTDOWN */}
      <section data-testid="roster-locked-notice" className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <Reveal>
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-crimson/40 bg-crimson/10 text-crimson-bright">
                  <Lock className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl">
                    Locked until <span className="text-crimson-bright">signing day concludes.</span>
                  </h2>
                </div>
              </div>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                We protect our signees. Out of respect for every athlete and family still going through the process,
                we don't release names, positions or commitment details before each sport's signing day concludes.
                Football and Basketball each have their own day — and each roster publishes the moment its window
                closes.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Want to be on this roster instead of waiting on it? The door is still open for the 2027 classes.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="glass rounded-2xl p-8 sm:p-10">
              <p className="eyebrow mb-2">Countdown to signing day</p>
              <p className="font-display text-2xl font-extrabold uppercase text-white">February 3, 2027</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                National Signing Day · Football
              </p>
              <div className="mt-6">
                <Countdown target={SIGNING_DAY} testid="roster-countdown" />
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-slate-500">
                Basketball rosters follow the conclusion of their respective signing period.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* POSITION BOARD */}
      <section data-testid="roster-position-slots" className="border-t border-white/5 bg-navy-850 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Who we're recruiting"
            title={
              <>
                Every position. <span className="text-ice">Still open.</span>
              </>
            }
            description="Until signing day locks each sport, every slot below is an opportunity. When the roster publishes, names replace the openings."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Reveal className="h-full">
              <SlotBoard title="Football — Class of 2027" slots={FOOTBALL_SLOTS} testid="roster-board-football" />
            </Reveal>
            <Reveal delay={0.12} className="h-full">
              <SlotBoard
                title="Basketball (Men's & Women's) — Class of 2027"
                slots={HOOPS_SLOTS}
                testid="roster-board-basketball"
              />
            </Reveal>
          </div>

          <Reveal delay={0.15} className="mt-16">
            <div className="clip-notch border border-ice/30 bg-ice/5 p-8 sm:p-10">
              <p className="font-display text-2xl font-extrabold uppercase text-white">Get the roster alert</p>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">
                We'll email you the moment each roster is published after signing day.
              </p>
              <div className="mt-6 max-w-xl">
                <NotifyForm interest="Roster Announcement" testidPrefix="roster-alert" cta="Alert Me" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* APPLY CTA */}
      <section className="border-t border-white/5 py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="clip-notch flex flex-col items-start justify-between gap-6 border border-ice/30 bg-ice/5 p-8 sm:flex-row sm:items-center">
              <div>
                <p className="font-display text-2xl font-extrabold uppercase text-white sm:text-3xl">
                  Your name belongs here.
                </p>
                <p className="mt-2 text-sm text-slate-300">
                  Enter your info on the Recruiting page and start the conversation.
                </p>
              </div>
              <Link to="/recruiting" data-testid="roster-recruiting-cta" className="btn-primary shrink-0">
                Get Recruited <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
